/**
 * Minimal CSV helpers for client-side exports — no dependency, RFC-4180-ish
 * quoting (fields containing commas, quotes, or newlines are double-quoted and
 * inner quotes doubled).
 */

// Excel, LibreOffice and Sheets evaluate a cell that begins with one of these as a formula, so a
// value carried through from user input can run on open — CWE-1236. Attendee names reach this
// export from the invite link, which is an anonymous surface, and the file is opened by a CS
// staffer, so the input is untrusted and the reader is privileged.
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/** Serialises one cell, quoting/escaping when needed and defusing formula triggers. */
const toCsvCell = (value: string | number | null): string => {
  const raw = value === null ? "" : String(value);

  // A leading apostrophe makes spreadsheets treat the rest as literal text. Prefer it to stripping
  // the character so the exported value still reads correctly to a human.
  const text = FORMULA_TRIGGERS.some((trigger) => raw.startsWith(trigger))
    ? `'${raw}`
    : raw;

  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** Builds CSV text from a header row and data rows (CRLF-separated). */
export const toCsv = (
  headers: string[],
  rows: Array<Array<string | number | null>>,
): string =>
  [headers, ...rows].map((row) => row.map(toCsvCell).join(",")).join("\r\n");

/** Triggers a browser download of `content` as `filename`. */
export const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
